// My profile (S-3; top bar → your name): your details, your password, 2-step sign-in (set up with a QR code, new
// recovery codes, turn off where the Hub allows it) and where you are signed in. On the fixtures (npm test) only the
// details from the dev user picker.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Badge, CopyButton, PageHeader, formatDateTime, formatRelative, notify } from '@/kit/ui'
import { apiMode, auth } from '@/lib/hubApi'
import { ROLES, profile, type MyProfile } from '@/lib/usersApi'
import { useSessionStore } from '@/store/sessionStore'
import { useCurrentUser } from '@/shell/useCurrentUser'

function Card({ title, hint, children, testId }: { title: string; hint?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="card max-w-3xl space-y-3" data-testid={testId}>
      <h2 className="section-title !mb-0">{title}</h2>
      {hint && <p className="text-2xs text-muted">{hint}</p>}
      {children}
    </section>
  )
}

const failed = (what: string) => (e: unknown) => notify.error(what, (e as Error).message)

export function ProfileScreen() {
  const { user } = useCurrentUser()
  const [me, setMe] = useState<MyProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = () => profile.get().then(setMe, (e: Error) => setError(e.message))
  useEffect(() => { if (apiMode()) void load() }, [])

  if (!apiMode()) {
    return (
      <>
        <PageHeader title="My profile" subtitle="Your details, password, 2-step sign-in and where you are signed in." />
        <Card title="Details"><p className="text-xs">{user.name} · {user.email} · {user.role}</p><p className="text-2xs text-muted">The rest needs the Hub API.</p></Card>
      </>
    )
  }
  return (
    <>
      <PageHeader title="My profile" subtitle="Your details, password, 2-step sign-in and where you are signed in." />
      {error && <p className="field-error">{error}</p>}
      {!me && !error && <p className="text-2xs text-muted">Loading…</p>}
      {me && (
        <div className="space-y-3">
          <Details me={me} onSaved={setMe} />
          <Password />
          <TwoStep me={me} onChanged={() => void load()} />
          <Sessions me={me} onChanged={setMe} />
        </div>
      )}
    </>
  )
}

function Details({ me, onSaved }: { me: MyProfile; onSaved: (p: MyProfile) => void }) {
  const [name, setName] = useState(me.name)
  const [busy, setBusy] = useState(false)
  const session = useSessionStore()
  const save = async () => {
    setBusy(true)
    try {
      const p = await profile.rename(name.trim())
      onSaved(p)
      // the top bar shows the new name at once
      if (session.view) useSessionStore.setState({ view: { ...session.view, user: { ...session.view.user, name: p.name } } })
      notify.success('Name saved')
    } catch (e) { failed('Not saved')(e) } finally { setBusy(false) }
  }
  const role = ROLES.find((r) => r.id === me.role)
  return (
    <Card title="Details" testId="profile-details">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[240px] flex-1"><label className="label" htmlFor="me-name">Name</label><input id="me-name" className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></div>
        <button type="button" className="btn btn-primary" disabled={busy || !name.trim() || name.trim() === me.name} onClick={() => void save()}>Save</button>
      </div>
      <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-muted">Email</dt><dd>{me.email} <span className="text-2xs text-muted">(your sign-in; an admin can change it only by adding you again)</span></dd>
        <dt className="text-muted">Role</dt><dd><Badge tone="primary">{role?.label ?? me.role}</Badge> <span className="text-2xs text-muted">{role?.can}</span></dd>
        <dt className="text-muted">Last sign-in</dt><dd>{formatDateTime(me.lastSignInAt)}</dd>
        <dt className="text-muted">Password changed</dt><dd>{me.passwordChangedAt ? formatDateTime(me.passwordChangedAt) : 'not yet'}</dd>
        <dt className="text-muted">Account made</dt><dd>{formatDateTime(me.createdAt)}</dd>
      </dl>
    </Card>
  )
}

function Password() {
  const accept = useSessionStore((s) => s.accept)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      accept(await auth.changePassword(current, next))
      setCurrent(''); setNext(''); setAgain('')
      notify.success('Password changed', 'Your other browsers were signed out.')
    } catch (err) { failed('Password not changed')(err) } finally { setBusy(false) }
  }
  return (
    <Card title="Password" hint="At least 12 characters, not your email. Changing it signs out your other browsers.">
      <form onSubmit={(e) => void submit(e)} className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="profile-password">
        <div><label className="label" htmlFor="me-current">Current password</label><input id="me-current" className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} /></div>
        <div><label className="label" htmlFor="me-new">New password</label><input id="me-new" className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} /></div>
        <div><label className="label" htmlFor="me-again">New password again</label><input id="me-again" className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} /></div>
        {next && again && next !== again && <p className="field-error sm:col-span-3">The two new passwords differ.</p>}
        <div className="sm:col-span-3"><button type="submit" className="btn btn-primary" disabled={busy || !current || next.length < 12 || next !== again}>Change password</button></div>
      </form>
    </Card>
  )
}

function TwoStep({ me, onChanged }: { me: MyProfile; onChanged: () => void }) {
  const accept = useSessionStore((s) => s.accept)
  const [setup, setSetup] = useState<{ secret: string; qr_svg?: string } | null>(null)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<'none' | 'codes' | 'off'>('none')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [busy, setBusy] = useState(false)
  const t = me.twoStep
  const run = async (what: string, work: () => Promise<void>) => {
    setBusy(true)
    try { await work() } catch (e) { failed(what)(e) } finally { setBusy(false) }
  }
  if (t.rule === 'off') return <Card title="2-step sign-in"><p className="text-xs text-muted">2-step sign-in is switched off on this Hub (ADMIN_TWO_STEP=off).</p></Card>

  if (!t.on) {
    return (
      <Card title="2-step sign-in" testId="profile-2step" hint="A 6-digit code from an app on your phone at every sign-in, so a stolen password is not enough. Google Authenticator, Microsoft Authenticator or a similar app.">
        <p className="text-xs"><Badge tone="warning">off</Badge> {t.rule === 'required' ? 'This Hub requires it: set it up now.' : 'Recommended for every admin.'}</p>
        {!setup ? (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run('Not started', async () => setSetup(await auth.setUpTwoStep()))} data-testid="2step-start">Set up 2-step sign-in</button>
        ) : (
          <div className="flex flex-wrap gap-4">
            {setup.qr_svg && <div className="h-44 w-44 shrink-0 rounded border border-slate-200 bg-white p-1" aria-label="QR code for the authenticator app" role="img" dangerouslySetInnerHTML={{ __html: setup.qr_svg }} data-testid="2step-qr" />}
            <div className="min-w-[220px] flex-1 space-y-2">
              <ol className="list-decimal space-y-1 pl-4 text-xs">
                <li>In the app: add an account → scan this QR code.</li>
                <li>No camera? Enter this key by hand (time-based): <span className="inline-flex items-center gap-1"><code className="mono break-all">{setup.secret}</code><CopyButton text={setup.secret} /></span></li>
                <li>Type the 6-digit code the app shows now.</li>
              </ol>
              <div className="flex items-end gap-2">
                <div><label className="label" htmlFor="me-2step-code">Code</label><input id="me-2step-code" className="input mono w-32" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} /></div>
                <button type="button" className="btn btn-primary" disabled={busy || code.trim().length < 6} data-testid="2step-confirm" onClick={() => void run('Not turned on', async () => {
                  // the recovery codes come back with the session and show in the "Your recovery codes" window
                  accept(await auth.confirmTwoStep(code.trim()))
                  setSetup(null); setCode('')
                  notify.success('2-step sign-in is on', 'Keep your recovery codes somewhere safe.')
                  onChanged()
                })}>Turn on</button>
              </div>
            </div>
          </div>
        )}
      </Card>
    )
  }

  return (
    <Card title="2-step sign-in" testId="profile-2step">
      <p className="text-xs"><Badge tone="success">on</Badge> since {formatDateTime(t.since)} · <b>{t.recoveryCodesLeft}</b> recovery code(s) left {t.recoveryCodesLeft <= 3 && <span className="text-amber-600 dark:text-amber-300">— make new ones</span>}</p>
      {codes && (
        <div className="rounded-md border border-accent/60 bg-accent-soft p-3" data-testid="new-recovery-codes">
          <p className="mb-1 text-xs font-semibold">Your new recovery codes · shown once</p>
          <ul className="mono grid grid-cols-2 gap-1 text-xs sm:grid-cols-5">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
          <p className="mt-1 text-2xs text-muted">Each works once, when you have no phone. The old codes no longer work.</p>
          <div className="mt-1 flex gap-2"><CopyButton text={codes.join('\n')} /><button type="button" className="btn btn-secondary btn-sm" onClick={() => setCodes(null)}>I have saved them</button></div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setMode(mode === 'codes' ? 'none' : 'codes'); setCode('') }} data-testid="2step-new-codes">New recovery codes</button>
        {t.rule !== 'required' && <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setMode(mode === 'off' ? 'none' : 'off'); setCode(''); setPassword('') }} data-testid="2step-off">Turn off</button>}
      </div>
      {t.rule === 'required' && <p className="text-2xs text-muted">This Hub requires 2-step sign-in, so it cannot be turned off. Lost your phone? Sign in with a recovery code, or ask an admin to reset your 2-step.</p>}
      {mode !== 'none' && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 p-2 dark:border-slate-700">
          {mode === 'off' && <div><label className="label" htmlFor="me-off-password">Your password</label><input id="me-off-password" className="input w-56" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></div>}
          <div><label className="label" htmlFor="me-code">Code from your app</label><input id="me-code" className="input mono w-32" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} /></div>
          {mode === 'codes' ? (
            <button type="button" className="btn btn-primary" disabled={busy || code.trim().length < 6} data-testid="2step-codes-make" onClick={() => void run('No new codes', async () => {
              setCodes((await profile.newRecoveryCodes(code.trim())).recovery_codes)
              setMode('none'); setCode('')
              onChanged()
            })}>Make new codes</button>
          ) : (
            <button type="button" className="btn btn-danger" disabled={busy || !password || code.trim().length < 6} data-testid="2step-off-confirm" onClick={() => void run('Not turned off', async () => {
              await profile.twoStepOff(password, code.trim())
              setMode('none'); setCode(''); setPassword('')
              notify.success('2-step sign-in is off')
              onChanged()
            })}>Turn off 2-step</button>
          )}
        </div>
      )}
    </Card>
  )
}

function Sessions({ me, onChanged }: { me: MyProfile; onChanged: (p: MyProfile) => void }) {
  const [busy, setBusy] = useState(false)
  const run = async (what: string, work: () => Promise<MyProfile>) => {
    setBusy(true)
    try { onChanged(await work()); notify.success(what) } catch (e) { failed('Not signed out')(e) } finally { setBusy(false) }
  }
  const others = me.sessions.filter((s) => !s.current).length
  return (
    <Card title="Where you are signed in" hint="Each browser you signed in with, until you sign out or it is idle for 30 minutes. Addresses are cut short (never the full IP).">
      <div className="overflow-x-auto">
        <table className="table" data-testid="profile-sessions">
          <thead><tr><th>Browser</th><th>Network</th><th>Signed in</th><th>Last used</th><th /></tr></thead>
          <tbody>
            {me.sessions.map((s) => (
              <tr key={s.id}>
                <td>{s.device ?? 'a browser'} {s.current && <Badge tone="success">this browser</Badge>}</td>
                <td className="mono text-2xs">{s.ipMasked ?? '—'}</td>
                <td className="text-2xs">{formatDateTime(s.createdAt)}</td>
                <td className="text-2xs">{formatRelative(s.lastSeenAt)}</td>
                <td className="text-right">{!s.current && <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void run('Signed out there', () => profile.endSession(s.id))}>Sign out</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {others > 0 && <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void run('Signed out everywhere else', () => profile.signOutOthers())} data-testid="sign-out-others">Sign out everywhere else ({others})</button>}
    </Card>
  )
}
